// „Запис на доставката" — последната стъпка: за коя дата е документът, номерът
// (задължителен само за фактура) и дали е платена още сега (начин на плащане).
// Датата стои тук, а не в хедъра: тя определя as-of наличността и при задна
// дата нулира натрупания недостиг — пита се съзнателно, веднъж, при записа.
// Продукт на минус от продажби → подсказка под датата (не забрана): с дата след
// продажбите минусът се отписва и доставката влиза цяла, с датата им — покрива го.
// Бъдеща дата не се приема (календарът не я дава; BE връща 400): стоката още не
// е пристигнала, а наличността би се вдигнала „отнапред".
// antd Modal: Esc = Назад, Enter / Ctrl+Enter = Запиши.
import { useEffect, useState } from "react";
import { DatePicker, Modal } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { DeliveryDocumentType, DeliveryPaymentMethod } from "../../../../api/inventory";
import { modalStyles } from "../../../../utils/modalStyles";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { SegmentedCounts } from "../../components/list/ListBits";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { formatMoney } from "../../components/numbers";
import { DeliveryTypeBadge } from "./DeliveryBadges";
import { DeliveryNegativeStockHint } from "./DeliveryNegativeStockHint";
import type { IncomingLine } from "./deliveryNegativeHint";
import { PAYMENT_METHODS } from "./deliveryMeta";

export type DeliverySaveValues = {
  date: string; // YYYY-MM-DD
  documentNumber: string;
  paid: boolean;
  method: DeliveryPaymentMethod;
};

type Props = {
  open: boolean;
  documentType: DeliveryDocumentType;
  initial: DeliverySaveValues;
  // плащането се пита само при нова доставка (редакцията не пипа плащането)
  askPayment: boolean;
  linesCount: number;
  totalGross: number;
  saving: boolean;
  // редовете на записа — за подсказката „продадено без наличност"
  incomingLines?: IncomingLine[];
  // редакция: минусът се гледа без самата доставка
  editMovementId?: number | null;
  onBack: () => void;
  onSave: (values: DeliverySaveValues) => void;
};

const isFutureDay = (day: dayjs.Dayjs) => day.isAfter(dayjs(), "day");

export const DeliverySaveModal = ({
  open,
  documentType,
  initial,
  askPayment,
  linesCount,
  totalGross,
  saving,
  incomingLines = [],
  editMovementId = null,
  onBack,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [values, setValues] = useState<DeliverySaveValues>(initial);
  const [numberError, setNumberError] = useState(false);
  const [dateError, setDateError] = useState(false);
  useEffect(() => {
    if (open) {
      setValues(initial);
      setNumberError(false);
      setDateError(false);
    }
    // стойностите се поемат при отваряне
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isInvoice = documentType === "invoice";
  const save = () => {
    if (isInvoice && !values.documentNumber.trim()) {
      setNumberError(true);
      return;
    }
    if (!values.date) return;
    // и при редакция на документ, записан преди правилото, с бъдеща дата
    if (isFutureDay(dayjs(values.date))) {
      setDateError(true);
      return;
    }
    onSave(values);
  };
  const labelClass = "text-[11px] font-semibold uppercase tracking-[0.1em] text-muted";
  const inputClass = `w-full rounded-lg border bg-surface px-[11px] py-[9px] font-sheet text-ink outline-none focus:border-brand ${
    isNarrow ? "text-base" : "text-sm"
  }`;

  return (
    <Modal
      open={open}
      onCancel={onBack}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={460}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      <div
        className="flex flex-col bg-surface"
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          // Enter в отворения календар е негов
          if ((event.target as HTMLElement).closest(".ant-picker-dropdown")) return;
          event.preventDefault();
          save();
        }}
      >
        <div className="flex flex-col gap-[3px] border-b border-shade-2 px-[22px] py-[18px]">
          <span className="text-[17px] font-semibold text-ink">{t("inventoryPage.saveModalTitle")}</span>
          <span className="text-[13px] text-body">
            {t("inventoryPage.homeDraftLines", { count: linesCount })}
          </span>
        </div>
        <div className="flex flex-col gap-3.5 px-[22px] py-4">
          <div className="flex items-center gap-2">
            <DeliveryTypeBadge documentType={documentType} />
            <span className="text-[13px] text-body">
              {isInvoice ? t("inventoryPage.saveTypeHintInvoice") : t("inventoryPage.saveTypeHintNote")}
            </span>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>
              {t("inventoryPage.saveDateLabel")} <span className="text-danger-ink">*</span>
            </span>
            <DatePicker
              autoFocus
              value={values.date ? dayjs(values.date) : null}
              onChange={(day) => {
                setValues({ ...values, date: day ? day.format("YYYY-MM-DD") : "" });
                setDateError(false);
              }}
              disabledDate={isFutureDay}
              status={dateError ? "error" : undefined}
              format="DD.MM.YYYY"
              allowClear={false}
              className="w-full"
            />
            {dateError && <span className="text-xs text-danger-ink">{t("inventoryPage.saveDateFuture")}</span>}
          </label>
          {open && (
            <DeliveryNegativeStockHint date={values.date} lines={incomingLines} editMovementId={editMovementId} />
          )}
          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>
              {isInvoice ? t("inventoryPage.fieldInvoice") : t("inventoryPage.fieldNoteNo")}
              {isInvoice && <span className="text-danger-ink"> *</span>}
            </span>
            <input
              value={values.documentNumber}
              onChange={(event) => {
                setValues({ ...values, documentNumber: event.target.value });
                setNumberError(false);
              }}
              placeholder={isInvoice ? t("inventoryPage.fieldInvoicePh") : t("inventoryPage.optional")}
              className={`${inputClass} ${numberError ? "border-danger" : "border-shade-3"}`}
            />
            {numberError && (
              <span className="text-xs text-danger-ink">{t("inventoryPage.saveNumberRequired")}</span>
            )}
          </label>
          {askPayment && (
            <>
              <div className="flex flex-col gap-1.5">
                <span className={labelClass}>{t("inventoryPage.colPayment")}</span>
                <SegmentedCounts
                  fill
                  value={values.paid ? "paid" : "unpaid"}
                  onChange={(key) => setValues({ ...values, paid: key === "paid" })}
                  options={[
                    { key: "unpaid", label: t("inventoryPage.payUnpaid") },
                    { key: "paid", label: t("inventoryPage.payPaid") },
                  ]}
                />
              </div>
              {values.paid && (
                <div className="flex flex-col gap-1.5">
                  <span className={labelClass}>{t("inventoryPage.payMethodLong")}</span>
                  <SegmentedCounts
                    fill
                    value={values.method}
                    onChange={(method) => setValues({ ...values, method })}
                    options={PAYMENT_METHODS.map((key) => ({
                      key,
                      label: t(`inventoryPage.payMethod_${key}`),
                    }))}
                  />
                </div>
              )}
            </>
          )}
          <div className="flex justify-between rounded-[9px] bg-fill px-[13px] py-[11px]">
            <span className="text-body">{t("inventoryPage.homeDraftLines", { count: linesCount })}</span>
            <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totalGross)}</span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-shade-2 px-[22px] pb-[18px] pt-3.5">
          <button type="button" onClick={onBack} className={SECONDARY_BUTTON_CLASS}>
            {t("inventoryPage.back")}
          </button>
          <button type="button" disabled={saving} onClick={save} className={PRIMARY_BUTTON_CLASS}>
            {t("inventoryPage.saveModalSave")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeliverySaveModal;
